import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { App, EasSetup } from '@hangar/shared';
import { ApiError } from '@/api/client';
import { useEasSetup, useProvisionEas, useRegisterEasWebhook } from '@/api/hooksEas';
import { stagger } from '@/lib/motion';
import { useCanConfigure } from '@/lib/permissions';
import { CopyButton } from '@/components/CopyButton';
import { ErrorState } from '@/components/ErrorState';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Tag } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { EAS_SOURCE_LABEL, appJsonSnippet, easFixOf, hasEasProject } from '@/features/apps/easSetupModel';
import { WebhookStatus } from '@/features/apps/WebhookStatus';
import { webhookActionOf } from '@/features/apps/webhookStatusModel';

/** The app's EAS project (platform operators, SPEC decision 23): created by Hangar in the client's own Expo organization (SPEC decision 19), and the app.json the repository needs. */
export function EasProjectCard({ app }: { app: App }) {
  const setup = useEasSetup(app.id, hasEasProject(app.framework));
  if (!hasEasProject(app.framework)) return null;
  // An API without the route (404/501) has nothing to show here: the settings card still lists the id.
  if (setup.error instanceof ApiError && (setup.error.status === 404 || setup.error.status === 501)) return null;
  return (
    <Card className="enter" style={stagger(2)} data-testid="app-eas-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon name="rocket_launch" size={18} className="text-subtle" />
          EAS project
        </CardTitle>
        <CardDescription>Hangar creates and manages it in your organization&apos;s Expo account: no expo.dev steps</CardDescription>
      </CardHeader>
      <CardContent>
        {setup.isLoading && !setup.data ? <Skeleton className="h-16 w-full" aria-busy="true" /> : null}
        {setup.isError && !setup.data ? <ErrorState compact error={setup.error} onRetry={() => void setup.refetch()} /> : null}
        {setup.data ? (setup.data.configured ? <Linked app={app} setup={setup.data} /> : <CreateProject app={app} setup={setup.data} />) : null}
      </CardContent>
    </Card>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

/** The project is linked: where it lives, how it got there, and the app.json to commit. */
function Linked({ app, setup }: { app: App; setup: EasSetup }) {
  const snippet = appJsonSnippet(setup);
  return (
    <div className="flex flex-col gap-4" data-testid="app-eas-linked">
      <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-3">
        <Fact label="Project">
          {setup.dashboardUrl && setup.fullName ? (
            <a href={setup.dashboardUrl} target="_blank" rel="noreferrer" className="inline-flex max-w-full items-center gap-1 truncate font-mono text-[13px] text-foreground underline-offset-2 hover:underline" data-testid="app-eas-project-link">
              <span className="truncate">{setup.fullName}</span>
              <Icon name="open_in_new" size={14} className="shrink-0 text-subtle" />
            </a>
          ) : (
            <span className="text-[13px] text-subtle">owner and slug unknown</span>
          )}
        </Fact>
        <Fact label="Project id">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="block truncate font-mono text-[13px] text-foreground" title={setup.projectId ?? undefined}>
              {setup.projectId}
            </span>
            {setup.projectId ? <CopyButton text={setup.projectId} size="icon-sm" label="Copy EAS project id" /> : null}
          </span>
        </Fact>
        <Fact label="Source">{setup.source ? <Tag>{EAS_SOURCE_LABEL[setup.source]}</Tag> : null}</Fact>
      </dl>
      <WebhookStatus webhook={setup.webhook} provider="EAS" />
      <WebhookAction app={app} setup={setup} />
      {snippet ? <AppJson snippet={snippet} manual={setup.source === 'manual'} /> : null}
    </div>
  );
}

/** "Register webhook" / "Repair webhook" while Hangar's webhook is not registered; works for a project linked by hand too. */
function WebhookAction({ app, setup }: { app: App; setup: EasSetup }) {
  const can = useCanConfigure(app.projectId);
  const register = useRegisterEasWebhook();
  const action = webhookActionOf(setup.webhook);
  if (!action || app.archivedAt) return null;
  const fix = register.error ? easFixOf(register.error) : null;
  return (
    <div className="flex flex-col gap-2" data-testid="app-eas-webhook-action">
      <ProblemAlert error={register.error} testId="app-eas-webhook-error" />
      <div className="flex flex-wrap items-center gap-2">
        <GatedButton can={can} loading={register.isPending} variant="outline" data-testid="app-eas-webhook-button" onClick={() => register.mutate({ appId: app.id })}>
          <Icon name="webhook" size={18} />
          {action.label}
        </GatedButton>
        {fix === 'integrations' ? (
          <Button asChild variant="outline">
            <Link to="/integrations">
              Open Integrations
              <Icon name="arrow_forward" size={16} />
            </Link>
          </Button>
        ) : null}
      </div>
    </div>
  );
}

const APP_JSON_NOTE = { manual: 'Your app.json must carry this project id.', created: 'Commit this in the app’s app.json (or app.config.*): eas-cli checks the owner, the slug and the id.' };

/** What the app's own app.json must carry, with a copy button; `note` replaces the line above it (the client's wording). */
export function AppJson({ snippet, manual, note }: { snippet: string; manual: boolean; note?: string }) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border bg-background p-3" data-testid="app-eas-appjson">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[13px] text-muted-foreground">{note ?? (manual ? APP_JSON_NOTE.manual : APP_JSON_NOTE.created)}</p>
        <CopyButton text={snippet} label="Copy" testId="app-eas-copy-appjson" />
      </div>
      <pre className="overflow-x-auto font-mono text-[12px] leading-5 text-foreground">{snippet}</pre>
    </div>
  );
}

/** No project yet: one button, and the way out of each failure (Integrations, another slug, try again). */
function CreateProject({ app, setup }: { app: App; setup: EasSetup }) {
  const can = useCanConfigure(app.projectId);
  const provision = useProvisionEas();
  const proposed = setup.proposedSlug ?? app.slug;
  const [slug, setSlug] = useState(proposed);
  const fix = provision.error ? easFixOf(provision.error) : null;
  return (
    <div className="flex flex-col gap-3" data-testid="app-eas-create">
      <p className="text-[13px] text-muted-foreground">
        This app has no EAS project yet. Hangar creates <span className="font-mono text-foreground">{proposed}</span> on Expo (the client’s name first) and fills in its id.
      </p>
      {fix === 'slug' ? (
        <label className="flex max-w-sm flex-col gap-1 text-[13px]">
          <span className="text-muted-foreground">Project slug (must equal expo.slug in the app config)</span>
          <Input data-testid="app-eas-slug" className="font-mono sm:text-[13px]" value={slug} spellCheck={false} maxLength={100} onChange={(e) => setSlug(e.target.value.trim())} />
        </label>
      ) : null}
      <ProblemAlert error={provision.error} testId="app-eas-error" />
      <div className="flex flex-wrap items-center gap-2">
        <GatedButton can={can} loading={provision.isPending} data-testid="app-eas-create-button" onClick={() => provision.mutate({ appId: app.id, ...(fix === 'slug' && slug && slug !== proposed ? { body: { slug } } : {}) })}>
          <Icon name={fix ? 'refresh' : 'add'} size={18} />
          {fix ? 'Try again' : 'Create EAS project'}
        </GatedButton>
        {fix === 'integrations' ? (
          <Button asChild variant="outline">
            <Link to="/integrations">
              Open Integrations
              <Icon name="arrow_forward" size={16} />
            </Link>
          </Button>
        ) : null}
      </div>
    </div>
  );
}
